import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, type FieldErrors, useForm, useWatch } from 'react-hook-form';
import { CiWarning } from 'react-icons/ci';
import { FiLock, FiUsers } from 'react-icons/fi';
import { z } from 'zod';

import { DateTimePicker, Modal, Toggle, WeekdaySelector } from '@/components';
import { Button, ButtonOutlined } from '@/components/Button';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import type { ReviewItem } from '@/components/EventCard/EventWizardModal/eventWizard.types';
import { fmtRange, fmtWhen } from '@/components/EventCard/EventWizardModal/eventWizard.utils';
import { StepReview } from '@/components/EventCard/EventWizardModal/steps/StepReview';
import { WizardStepper } from '@/components/EventCard/EventWizardModal/WizardStepper';
import { Autocomplete, DurationInput, Input, Select } from '@/components/Inputs';
import formStyles from '@/components/Inputs/inputs.module.scss';
import ParticipantBadge from '@/components/ParticipantBadge/ParticipantBadge';
import { ChecklistEditor, fromDrafts, toDrafts } from '@/components/TaskChecklist';
import { TaskFiles } from '@/components/TaskFiles';
import { useIsServiceProvider } from '@/hooks/useIsServiceProvider';
import { Can } from '@/permissions/Can';
import { PERMISSIONS } from '@/permissions/permissions.catalog';
import { useAuth } from '@/store/auth/auth.store';
import { useAwayLabels } from '@/store/leaves/leaves.store';
import type { Person } from '@/store/persons/person.dto';
import { mapPeopleForSelection } from '@/store/persons/person.mapper';
import { usePerson } from '@/store/persons/person.store';
import {
  CHECKLIST_ITEMS_MAX,
  CHECKLIST_TEXT_MAX,
  TASK_PRIORITIES,
  TODO_DONE_STATUSES,
  type ChecklistItem,
  type TaskDocument,
  type TaskParticipant,
  TaskParticipantType,
  TaskPriority,
  type TaskResponse,
} from '@/store/tasks/tasks.dto';
import {
  TaskDocumentsNotAttachedError,
  TaskParticipantsNotAttachedError,
  useTasks,
} from '@/store/tasks/tasks.store';
import type {
  ActivityGenerator,
  CreateActivityGeneratorPayload,
  Equipment,
} from '@/store/vessels/vessel.dto';
import { useVessels } from '@/store/vessels/vessels.store';
import { offsetDate, snapToNextSlot } from '@/utils/dateSlots';
import { roleToParticipantType } from '@/utils/roles';
import { parseDurationToMinutes } from '@/utils/timeFormatter';
import { toast } from '@/utils/toast';

import { RichTextArea } from '../RichTextArea/RichTextArea';

import { REPEAT_OPTIONS, RepeatOption, TASK_STATUSES, TASK_TYPES } from './constants';
import styles from './CreateSheet.module.scss';
import PeopleSelectionModal from './PeopleSelectionModal';
import { isRepeatableTask, mapFormToActivityGenerator, mapFormToSingleTask } from './taskMappers';

const createTaskSchema = (requireEquipment = false) =>
  z
    .object({
      taskName: z
        .string()
        .min(1, 'Please give the task a name.')
        // The service's own limit: a longer name comes back as a 400 on save.
        .max(100, 'Please keep the task name to 100 characters.'),
      status: z.string().optional(),
      // Required by the service (`oneof=low medium high`), so it is never optional here:
      // a blank comes back as a 400 on save.
      priority: z.string().min(1, 'Please choose a priority.'),
      taskType: z.string().optional(),
      description: z.string().optional(),
      repeat: z.string().optional(),
      equipmentId: z.string().optional(),
      // Weekday indices only (0-6): the vessel API validates `days` with `gte=0,lte=6`.
      days: z.array(z.number().int().min(0).max(6)).optional(),
      startDateTime: z.string(),
      deadlineDateTime: z.string().optional(),
      duration: z.number().optional(),
      // Empty means "not priced yet", which is distinct from a zero-cost job, so the
      // field stays nullable rather than coercing a blank input to 0.
      cost: z.number().min(0, 'Please enter a cost of 0 or more.').nullable().optional(),
      // The editor's own limits (maxLength, the add button hiding at the cap) keep these
      // from being hit; the schema states them so a save cannot get past them either.
      checklist: z
        .array(
          z.object({
            draftId: z.string(),
            // The service's own; listed so validation keeps it and a save sends it back.
            id: z.string().optional(),
            text: z.string().max(CHECKLIST_TEXT_MAX),
            status: z.enum(['open', 'done']),
          }),
        )
        .max(CHECKLIST_ITEMS_MAX),
    })
    .superRefine((data, ctx) => {
      if (requireEquipment && !data.equipmentId) {
        ctx.addIssue({
          code: 'custom',
          message: 'Please choose the equipment.',
          path: ['equipmentId'],
        });
      }

      const isRepeating = data.repeat && data.repeat !== RepeatOption.NO_REPEAT;

      if (isRepeating) {
        // Validate duration for repeating tasks
        if (!data.duration || data.duration <= 0) {
          ctx.addIssue({
            code: 'custom',
            message: 'Please enter a duration.',
            path: ['duration'],
          });
        }

        if (data.repeat === RepeatOption.SPECIFIC_DAYS && (!data.days || data.days.length === 0)) {
          ctx.addIssue({
            code: 'custom',
            message: 'Please pick the weekdays for this series.',
            path: ['days'],
          });
        }
      } else {
        // Validate dates for non-repeating tasks
        if (!data.startDateTime || data.startDateTime.length === 0) {
          ctx.addIssue({
            code: 'custom',
            message: 'Please choose a start date and time.',
            path: ['startDateTime'],
          });
        }

        if (!data.deadlineDateTime || data.deadlineDateTime.length === 0) {
          ctx.addIssue({
            code: 'custom',
            message: 'Please enter an end date and time.',
            path: ['deadlineDateTime'],
          });
        }

        // Date comparison - only if both dates are provided and valid
        if (data.startDateTime && data.deadlineDateTime) {
          const start = new Date(data.startDateTime).getTime();
          const end = new Date(data.deadlineDateTime).getTime();

          if (!Number.isNaN(start) && !Number.isNaN(end)) {
            if (end <= start) {
              ctx.addIssue({
                code: 'custom',
                message: 'The end date and time is before the start.',
                path: ['deadlineDateTime'],
              });
            }
          }
        }
      }
    });

export type CreateTaskFormValues = z.infer<ReturnType<typeof createTaskSchema>>;

export type TaskSubmissionPayload =
  | { type: 'single'; data: CreateTaskPayload }
  | { type: 'repeatable'; data: CreateActivityGeneratorPayload };

export type CreateTaskPayload = {
  id?: string;
  title: string;
  description: string;
  location: {
    name: string;
    address: string;
    latitude: number;
    longitude: number;
    map_url: string;
  };
  type: string;
  status: string;
  priority: string;
  equipment?: {
    id: string;
    name: string;
  } | null;
  start: string;
  deadline: string;
  reminder: string;
  time_slot: {
    type: string;
    start: string;
    end: string;
  };
  responsible: TaskParticipant | null;
  participants?: TaskParticipant[];
  visibility?: 'private' | 'public';
  event_id?: string;
  project_id?: string;
  cost?: number | null;
  /** Taken by a create only; an edit saves its steps through `saveChecklist`. */
  checklist?: ChecklistItem[];
  /**
   * Files to attach once the task exists — they can't be uploaded before it has an
   * id, so `createTask` sends them separately rather than in the JSON body.
   */
  documents?: File[];
};

type CreateEditTaskSheetProps = {
  open: boolean;
  onClose: () => void;
  onSubmit?: (payload: TaskSubmissionPayload) => Promise<void>;
  onSubmitTask?: (payload: CreateTaskPayload) => Promise<void>;
  title: string;
  assigneeId?: string;
  assigneeName?: string;
  showAssignee?: boolean;
  equipment?: {
    id: string;
    name: string;
  } | null;
  equipmentId?: string;
  initialData?: TaskResponse & { duration?: string };
  editMode?: 'single' | 'all';
  eventId?: string;
  hideEquipment?: boolean;
  requireEquipment?: boolean;
  showCost?: boolean;
  hideRepeat?: boolean;
  /**
   * Drop the Priority field. The sheet doubles as the editor for equipment jobs and
   * service requests, where urgency is carried by the schedule or by the request
   * itself rather than chosen per task — and a control that means nothing on the
   * surface showing it is worse than no control. The value still travels: the form
   * keeps whatever the record holds (Medium for a new one), which is what the service
   * requires on save.
   */
  hidePriority?: boolean;
  /**
   * Offer only To do and Done. A maintenance job is either outstanding or finished; In
   * Progress and On Review describe delegated task workflow, which the schedule does not
   * model. A task carrying equipment — a maintenance job or a service request — takes
   * the same cut without the flag, so this is only needed where equipment is absent.
   */
  hideWorkflowStatuses?: boolean;
  /**
   * The caller only collects the task into a form it saves later (an event being
   * created or edited). Nothing is saved here, so there is no success toast, and no
   * Files field: a file can only be attached to a task that already exists.
   */
  staged?: boolean;
  /** The event's dates: the picker starts on them and stays inside them. */
  eventDates?: { start: Date | null; end: Date | null };
};

const calendarId = '019b5646-aad4-7756-934a-3df1a3e191d8';

type CreateEditTaskSheetContentProps = Omit<CreateEditTaskSheetProps, 'open'> & {
  people: Person[] | null;
};

type StepId = 'task' | 'when' | 'people' | 'details';

type ReviewRow = { label: string; value: string; step: StepId };

const STEP_LABELS: Record<StepId, string> = {
  task: 'Task',
  when: 'When',
  people: 'People',
  details: 'Details',
};

const SP_ALLOWED_STATUSES = ['in_progress', 'on_approval'];

/**
 * Works out which participants were added and which were removed by comparing
 * the task's original participants with the current selection.
 */
const getParticipantChanges = (
  originalParticipants: TaskParticipant[],
  currentParticipants: TaskParticipant[],
) => {
  const originalIds = new Set(originalParticipants.map((p) => p.person_id));
  const currentIds = new Set(currentParticipants.map((p) => p.person_id));
  return {
    addedParticipants: currentParticipants.filter((p) => !originalIds.has(p.person_id)),
    removedParticipantIds: originalParticipants
      .filter((p) => !currentIds.has(p.person_id))
      .map((p) => p.person_id),
  };
};

const CreateEditTaskSheetContent = ({
  onClose,
  onSubmit: onSubmitCallback,
  onSubmitTask,
  title,
  assigneeId,
  assigneeName,
  showAssignee,
  equipment,
  equipmentId,
  initialData,
  editMode,
  people,
  eventId,
  hideEquipment,
  requireEquipment,
  showCost,
  hideRepeat,
  hidePriority,
  hideWorkflowStatuses,
  staged,
  eventDates,
}: CreateEditTaskSheetContentProps) => {
  const { isServiceProvider } = useIsServiceProvider();
  const session = useAuth((state) => state.session);
  const currentUserId = session?.id;
  const {
    addTaskParticipants,
    deleteTaskParticipants,
    attachTaskDocuments,
    deleteTaskDocument,
    saveChecklist,
  } = useTasks();
  // The checklist as the service holds it once a save here has synced it. A retry after a
  // later step of that save failed starts from this, not from the list the sheet opened with:
  // that one still has the steps it deleted (a 404 on every retry) and lacks the ones it added
  // (created twice).
  const syncedChecklist = useRef<ChecklistItem[] | null>(null);
  const { getEquipment, ensureMaintenanceId, getSingleActivityGenerator } = useVessels();
  const [equipmentList, setEquipmentList] = useState<Equipment[]>([]);
  const [equipmentSearch, setEquipmentSearch] = useState('');
  const [generator, setGenerator] = useState<ActivityGenerator | null>(null);

  const [selectedPeople, setSelectedPeople] = useState<
    { person_id: string; type: string; name: string }[]
  >(() => {
    if (initialData?.participants && initialData.participants.length > 0) {
      const responsibleParticipant =
        initialData.responsible && typeof initialData.responsible === 'object'
          ? initialData.responsible
          : null;
      return initialData.participants
        .filter((p) => p.person_id !== responsibleParticipant?.person_id)
        .map((p) => ({
          person_id: p.person_id,
          type: p.type,
          name: p.name || 'Unknown',
        }));
    }

    return [];
  });
  const [isPrivate, setIsPrivate] = useState(initialData?.visibility === 'private');
  // Files are staged like every other field on this form: picking one doesn't
  // upload it and removing one doesn't detach it — saving does both, against the
  // task's id (existing task) or the one the create call comes back with.
  const [documents, setDocuments] = useState<TaskDocument[]>(initialData?.documents ?? []);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [removedDocumentIds, setRemovedDocumentIds] = useState<string[]>([]);
  const [responsible, setResponsible] = useState<TaskParticipant | null>(() => {
    if (initialData?.responsible && typeof initialData.responsible === 'object')
      return {
        ...initialData.responsible,
        type: initialData.responsible.type || TaskParticipantType.CREW,
      };
    if (assigneeId)
      return {
        person_id: assigneeId,
        type: TaskParticipantType.CREW,
        status: null,
        name: assigneeName,
      };
    return null;
  });
  const [initialResponsibleId] = useState(responsible?.person_id ?? null);

  const originalParticipants = useMemo<TaskParticipant[]>(() => {
    const responsibleId =
      initialData?.responsible && typeof initialData.responsible === 'object'
        ? initialData.responsible.person_id
        : null;
    return (initialData?.participants ?? []).filter((p) => p.person_id !== responsibleId);
  }, [initialData]);

  useEffect(() => {
    getEquipment().then(setEquipmentList);
  }, [getEquipment]);

  // A provider only ever works in_progress / on_approval (SP_ALLOWED_STATUSES), which is
  // disjoint from To do / Done — trimming their list too would leave them nothing to
  // pick, so the two-state cut is the owner's view only. A task carrying equipment takes
  // the cut without being asked: it is a maintenance job or a service request, and
  // neither models the delegated-workflow states.
  const isEquipmentJob = Boolean(equipment || equipmentId || initialData?.equipment);
  const limitToTodoDone = (hideWorkflowStatuses || isEquipmentJob) && !isServiceProvider;

  const taskStatuses = useMemo(
    () =>
      TASK_STATUSES.filter(
        (s) =>
          !limitToTodoDone ||
          (TODO_DONE_STATUSES as readonly string[]).includes(s.id) ||
          // A job parked in an intermediate status before the cut existed keeps it in the
          // list while it is the applied value: the form has to state what is actually
          // set, not show a blank select over a status it would then resave.
          s.id === initialData?.status,
      ).map((s) => ({
        ...s,
        disabled: isServiceProvider && !SP_ALLOWED_STATUSES.includes(s.id),
      })),
    [isServiceProvider, limitToTodoDone, initialData?.status],
  );

  const mappedPeople = useMemo(() => mapPeopleForSelection(people ?? []), [people]);

  const assigneePerson = useMemo(
    () => (assigneeId ? mappedPeople.find((p) => p.id === assigneeId) : undefined),
    [assigneeId, mappedPeople],
  );
  const assigneeFallbackName = responsible?.name || assigneeName || 'Unknown';

  const currentUserPerson = useMemo(
    () => people?.find((p) => p.id === currentUserId) ?? null,
    [people, currentUserId],
  );

  // A private task is visible only to its creator, so whoever has it open is the creator —
  // and it is assigned to them, since nobody else could see or work it.
  const creatorAsResponsible = useMemo<TaskParticipant | null>(() => {
    if (!currentUserId) return null;
    const mapped = mappedPeople.find((p) => p.id === currentUserId);
    const name =
      [session?.person_info?.first_name, session?.person_info?.last_name]
        .filter(Boolean)
        .join(' ') ||
      [mapped?.firstName, mapped?.lastName].filter(Boolean).join(' ') ||
      'Unknown';
    return {
      person_id: currentUserId,
      type: roleToParticipantType(mapped?.role) as TaskParticipantType,
      status: null,
      name,
    };
  }, [currentUserId, session?.person_info, mappedPeople]);

  const pinnedPeople = useMemo<Person[]>(() => {
    // The creator, and only while creating: they are the one participant this form puts
    // on the task itself, so they are the only one it states up front and refuses to let
    // go. Owner and captain are attached by the backend to every task regardless — but
    // pinning them here claimed the form had added them, and made two people nobody
    // picked non-removable. They stay ordinary, selectable participants.
    if (initialData || !currentUserPerson) return [];
    return [currentUserPerson];
  }, [initialData, currentUserPerson]);

  const defaultValues = useMemo<CreateTaskFormValues>(() => {
    if (initialData) {
      const isEditAll = editMode === 'all';
      return {
        taskName: initialData.title ?? '',
        status: initialData.status ?? '',
        // Records written before the field was editable can carry a blank; showing Medium
        // means the form states what a save will write instead of posting an invalid value.
        priority: initialData.priority || TaskPriority.MEDIUM,
        taskType: initialData.type ?? '',
        description: initialData.description ?? '',
        repeat: isEditAll ? RepeatOption.EACH_DAY : RepeatOption.NO_REPEAT,
        days: [],
        startDateTime: initialData.time_slot?.start ?? '',
        deadlineDateTime: isEditAll ? '' : (initialData.time_slot?.end ?? ''),
        duration: initialData.duration ? parseDurationToMinutes(initialData.duration) : 0,
        equipmentId: initialData.equipment?.id ?? '',
        cost: initialData.cost ?? null,
        checklist: toDrafts(initialData.checklist),
      };
    }
    const defaultStart = eventDates?.start ?? snapToNextSlot();
    const defaultEnd = eventDates?.end ?? offsetDate(defaultStart, 15);
    return {
      taskName: '',
      status: TASK_STATUSES[0].id,
      priority: TaskPriority.MEDIUM,
      taskType: TASK_TYPES[0].id,
      description: '',
      repeat: RepeatOption.NO_REPEAT,
      days: [],
      startDateTime: defaultStart.toISOString(),
      deadlineDateTime: defaultEnd.toISOString(),
      equipmentId: equipmentId ?? equipment?.id ?? '',
      duration: 0,
      cost: null,
      checklist: [],
    };
  }, [initialData, editMode, equipmentId, equipment?.id, eventDates?.start, eventDates?.end]);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors, isSubmitting, isDirty, dirtyFields },
  } = useForm<CreateTaskFormValues>({
    resolver: zodResolver(createTaskSchema(requireEquipment)),
    defaultValues,
    mode: 'onChange',
  });

  const repeatValue = useWatch({
    control,
    name: 'repeat',
  });

  const daysValue = useWatch({
    control,
    name: 'days',
  });

  const startDateValue = useWatch({
    control,
    name: 'startDateTime',
  });

  const deadlineDateValue = useWatch({
    control,
    name: 'deadlineDateTime',
  });

  // Who is on leave over the task's days: listed last in the assignee and participant pickers,
  // their days beside them.
  const away = useAwayLabels(startDateValue, deadlineDateValue);

  const taskStatus = useWatch({ control, name: 'status' });
  const [taskName, priority, equipmentIdValue, cost, description, checklist] = useWatch({
    control,
    name: ['taskName', 'priority', 'equipmentId', 'cost', 'description', 'checklist'],
  });

  const isStartInPast = useMemo(() => {
    if (!startDateValue) return false;
    return new Date(startDateValue) < new Date();
  }, [startDateValue]);

  const buttonText =
    taskStatus === 'completed' && dirtyFields.status
      ? 'Complete Task'
      : initialData
        ? 'Save Task'
        : 'Add Task';

  // Revalidate deadline when start date changes
  useEffect(() => {
    if (startDateValue) {
      void trigger('deadlineDateTime');
    }
  }, [startDateValue, trigger]);

  // When editing an entire series, the recurrence, series start date, weekdays
  // and duration live on the activity generator — not on the selected task
  // instance. Fetch the generator and hydrate the form from its rules.
  useEffect(() => {
    if (editMode !== 'all' || !initialData?.generator_id) return;
    let cancelled = false;
    getSingleActivityGenerator(initialData.generator_id)
      .then((g) => {
        if (!cancelled) setGenerator(g);
      })
      .catch((err) => console.warn('Failed to load activity generator', err));
    return () => {
      cancelled = true;
    };
  }, [editMode, initialData?.generator_id, getSingleActivityGenerator]);

  useEffect(() => {
    const rules = generator?.time_slot_rules;
    if (!rules) return;
    if (rules.run_period && rules.run_period !== 'single') {
      setValue('repeat', rules.run_period, { shouldValidate: true });
    }
    if (rules.start) {
      setValue('startDateTime', rules.start, { shouldValidate: true });
    }
    // AI-generated series may carry month-day values (e.g. 17) that the weekday picker
    // cannot show and the API rejects (`lte=6`); drop them so the user can re-pick and save.
    setValue(
      'days',
      (rules.days ?? []).filter((d) => Number.isInteger(d) && d >= 0 && d <= 6),
    );
    if (rules.duration) {
      setValue('duration', parseDurationToMinutes(rules.duration), { shouldValidate: true });
    }
  }, [generator, setValue]);

  const handleDayToggle = (day: number) => {
    const currentDays = daysValue || [];
    const dayExists = currentDays.includes(day);

    if (dayExists) {
      setValue(
        'days',
        currentDays.filter((d) => d !== day),
        { shouldDirty: true },
      );
    } else {
      setValue('days', [...currentDays, day], { shouldDirty: true });
    }
  };

  const handleSelectPerson = (id: string) => {
    const person = mappedPeople.find((p) => p.id === id);
    const fullName = [person?.firstName, person?.lastName].filter(Boolean).join(' ') || 'Unknown';
    setSelectedPeople((prev) => {
      if (prev.some((p) => p.person_id === id)) return prev;
      return [
        ...prev,
        {
          person_id: id,
          type: roleToParticipantType(person?.role),
          name: fullName,
        },
      ];
    });
  };

  const handleRemovePerson = (id: string) => {
    setSelectedPeople((prev) => prev.filter((person) => person.person_id !== id));
  };

  const handleRemoveDocument = (documentId: string) => {
    setDocuments((prev) => prev.filter((doc) => doc.id !== documentId));
    setRemovedDocumentIds((prev) => [...prev, documentId]);
  };

  /**
   * A task being created has no id to upload against, so its files ride along with
   * the payload and the store attaches them the moment the create call answers. An
   * existing task is handled directly, above.
   */
  const withPendingFiles = (payload: CreateTaskPayload): CreateTaskPayload =>
    !initialData && pendingFiles.length ? { ...payload, documents: pendingFiles } : payload;

  const onSubmit = async (values: CreateTaskFormValues) => {
    try {
      const context = {
        calendarId,
        initialData,
        eventId,
      };

      // Get equipment from form value or fallback to prop
      const selectedEquipment = values.equipmentId
        ? (equipmentList.find((e) => e.id === values.equipmentId) ?? null)
        : (equipment ?? null);

      // const participants =
      //   responsible && !selectedPeople.some((p) => p.person_id === responsible.person_id)
      //     ? [
      //         ...selectedPeople,
      //         {
      //           person_id: responsible.person_id,
      //           type: mappedPeople.find((p) => p.id === responsible.person_id)?.role || 'responsible',
      //         },
      //       ]
      //     : selectedPeople;

      const participants = selectedPeople;

      // Backend requires responsible.name (non-empty). When the assignee is
      // pre-filled (assigneeId) the picker is hidden, so name may be missing —
      // resolve it from the people list, the assigneeName prop, then fall back.
      const responsibleWithName: TaskParticipant | null = isPrivate
        ? creatorAsResponsible
        : responsible
          ? {
              ...responsible,
              name:
                responsible.name ||
                (() => {
                  const p = mappedPeople.find((mp) => mp.id === responsible.person_id);
                  return [p?.firstName, p?.lastName].filter(Boolean).join(' ');
                })() ||
                assigneeName ||
                'Unknown',
            }
          : null;

      // On edit, reconcile participants against the ones the task started with:
      // add the newly selected, remove the deselected. (Repeatable tasks manage
      // participants through their generator, so skip them here.)
      if (
        initialData?.id &&
        !initialData.id.startsWith('temp-') &&
        !isRepeatableTask(values.repeat)
      ) {
        const { addedParticipants, removedParticipantIds } = getParticipantChanges(
          originalParticipants,
          participants as TaskParticipant[],
        );
        await Promise.all([
          addedParticipants.length && addTaskParticipants(initialData.id, addedParticipants),
          removedParticipantIds.length &&
            deleteTaskParticipants(initialData.id, removedParticipantIds),
          // Files hang off the task rather than travelling in its body, so the two
          // staged lists are applied here against the id it already has.
          ...removedDocumentIds.map((documentId) => deleteTaskDocument(initialData.id, documentId)),
          // Before the PUT, which ignores the checklist, so its answer carries the saved list.
          // A retry deletes the steps this one added and creates them again: the form's copies
          // have no ids. Same end state.
          // ponytail: a sync that itself fails partway retries from the list it started with, so
          // a step it already deleted 404s every retry until the sheet is reopened; refetching
          // the base with `fetchTaskById(id, { fresh: true })` on failure is the upgrade.
          saveChecklist(
            initialData.id,
            syncedChecklist.current ?? initialData.checklist ?? [],
            fromDrafts(values.checklist),
          ).then((saved) => {
            syncedChecklist.current = saved;
          }),
        ]);
        if (pendingFiles.length) {
          await attachTaskDocuments(initialData.id, pendingFiles);
        }
      }

      // New unified callback pattern
      if (onSubmitCallback) {
        if (isRepeatableTask(values.repeat)) {
          // Resolved here rather than on mount: the generator is rejected outright without
          // it, so it has to be a hard step of submitting — a missing plan surfaces as a
          // failed save the user can retry, not as a body posted without `maintenance_id`.
          const payload = mapFormToActivityGenerator(
            values,
            participants,
            selectedEquipment,
            await ensureMaintenanceId(),
            responsibleWithName,
            isPrivate,
          );
          await onSubmitCallback({ type: 'repeatable', data: payload });
        } else {
          const payload = mapFormToSingleTask(
            values,
            selectedEquipment,
            context,
            responsibleWithName,
            participants as TaskParticipant[],
            isPrivate,
          );
          await onSubmitCallback({ type: 'single', data: withPendingFiles(payload) });
        }
      } else if (onSubmitTask) {
        // Legacy callback - single tasks only
        const payload = mapFormToSingleTask(
          values,
          selectedEquipment,
          context,
          responsibleWithName,
          participants as TaskParticipant[],
          isPrivate,
        );
        await onSubmitTask(withPendingFiles(payload));
      }
      if (!staged) toast.success(`Your task has been ${initialData ? 'updated' : 'created'}.`);
      onClose();
    } catch (err: unknown) {
      console.error(err);
      // The task exists in this case — telling the user creation failed would make
      // them submit again and end up with a duplicate.
      toast.error(
        err instanceof TaskParticipantsNotAttachedError ||
          err instanceof TaskDocumentsNotAttachedError
          ? err.message
          : `Couldn’t ${initialData ? 'update' : 'create'} the task. Please try again.`,
      );
    }
  };

  const isEdit = !!initialData;
  const isSingle = repeatValue === RepeatOption.NO_REPEAT;
  // A private task has nobody to assign, so People drops out and the count says so.
  const steps: StepId[] = isPrivate
    ? ['task', 'when', 'details']
    : ['task', 'when', 'people', 'details'];
  const reviewStep = steps.length + 1;
  const [step, setStep] = useState(1);
  // Editing walks a task that is already whole, so every step is open from the start.
  const [furthest, setFurthest] = useState(isEdit ? steps.length : 1);
  const stepId: StepId | undefined = steps[step - 1];
  const isReview = !isEdit && step === reviewStep;

  const stepFields: Record<StepId, (keyof CreateTaskFormValues)[]> = {
    task: requireEquipment ? ['taskName', 'priority', 'equipmentId'] : ['taskName', 'priority'],
    when: ['repeat', 'startDateTime', 'deadlineDateTime', 'duration', 'days'],
    people: [],
    details: requireEquipment
      ? ['status', 'cost', 'description', 'checklist']
      : ['status', 'cost', 'description', 'equipmentId', 'checklist'],
  };
  const isTaskStepComplete = !!taskName?.trim() && (!requireEquipment || !!equipmentIdValue);

  // Focus follows the step, so a screen reader hears where Next or Back landed. Not on
  // open: the modal puts focus where it belongs then.
  const focusRef = useRef<HTMLElement>(null);
  const stepMovedRef = useRef(false);
  useEffect(() => {
    if (stepMovedRef.current) focusRef.current?.focus();
  }, [step]);

  const moveTo = (n: number) => {
    stepMovedRef.current = true;
    setStep(n);
    setFurthest((f) => Math.max(f, n));
  };

  const handleNext = async () => {
    if (stepId && !(await trigger(stepFields[stepId]))) return;
    moveTo(step + 1);
  };

  // A save from an early step can fail on a field further on; open the step that holds it.
  const onInvalid = (fieldErrors: FieldErrors<CreateTaskFormValues>) => {
    const index = steps.findIndex((id) => stepFields[id].some((field) => fieldErrors[field]));
    if (index >= 0) moveTo(index + 1);
  };
  const save = () => handleSubmit(onSubmit, onInvalid)();

  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const hasInput =
    isDirty ||
    isPrivate !== (initialData?.visibility === 'private') ||
    pendingFiles.length > 0 ||
    removedDocumentIds.length > 0 ||
    selectedPeople.length !== originalParticipants.length ||
    (responsible?.person_id ?? null) !== initialResponsibleId;
  const requestClose = () => (hasInput ? setConfirmDiscard(true) : onClose());

  const equipmentName =
    equipmentList.find((e) => e.id === equipmentIdValue)?.name ??
    (equipment?.id === equipmentIdValue ? equipment?.name : undefined);
  const fileCount = documents.length + pendingFiles.length;
  const stepCount = checklist?.filter((step) => step.text.trim()).length ?? 0;
  const reviewRows: (ReviewRow | false)[] = [
    { label: 'Task', value: taskName, step: 'task' },
    !hidePriority && {
      label: 'Priority',
      value: TASK_PRIORITIES.find((p) => p.id === priority)?.label ?? 'Not set',
      step: 'task',
    },
    {
      label: 'When',
      value: isSingle
        ? fmtRange(
            startDateValue ? new Date(startDateValue) : null,
            deadlineDateValue ? new Date(deadlineDateValue) : null,
          ) || 'Not set'
        : [
            REPEAT_OPTIONS.find((o) => o.id === repeatValue)?.label,
            startDateValue && `from ${fmtWhen(new Date(startDateValue))}`,
          ]
            .filter(Boolean)
            .join(', '),
      step: 'when',
    },
    isPrivate && { label: 'Visibility', value: 'Only you', step: 'task' },
    !isPrivate && {
      label: 'Assignee',
      value: responsible ? responsible.name || assigneeFallbackName : 'Not set',
      step: 'people',
    },
    !isPrivate && {
      label: 'Participants',
      value: selectedPeople.map((p) => p.name).join(', ') || 'None',
      step: 'people',
    },
    !hideEquipment &&
      !!equipmentName && {
        label: 'Equipment',
        value: equipmentName,
        step: requireEquipment ? 'task' : 'details',
      },
    {
      label: 'Status',
      value: taskStatuses.find((s) => s.id === taskStatus)?.label ?? 'Not set',
      step: 'details',
    },
    !!showCost &&
      isSingle && {
        label: 'Cost',
        value: cost == null ? 'Not set' : String(cost),
        step: 'details',
      },
    { label: 'Description', value: description ? 'Added' : 'None', step: 'details' },
    {
      label: 'Checklist',
      value: stepCount ? `${stepCount} step${stepCount === 1 ? '' : 's'}` : 'None',
      step: 'details',
    },
    isSingle &&
      !staged && {
        label: 'Files',
        value: fileCount ? `${fileCount} file${fileCount === 1 ? '' : 's'}` : 'None',
        step: 'details',
      },
  ];
  const reviewItems: ReviewItem[] = reviewRows
    .filter((row): row is ReviewRow => !!row)
    .map(({ step: rowStep, ...row }) => ({
      ...row,
      onClick: () => moveTo(steps.indexOf(rowStep) + 1),
    }));

  const equipmentField = (
    <Can permission={PERMISSIONS.vessel.Equipment}>
      {!hideEquipment && (
        <Controller
          name="equipmentId"
          control={control}
          render={({ field }) => {
            const selectedEquipmentName = field.value
              ? equipmentList.find((e) => e.id === field.value)?.name
              : null;

            const filteredEquipment = equipmentList.filter((e) =>
              e.name.toLowerCase().includes(equipmentSearch.toLowerCase()),
            );

            return (
              <Autocomplete
                label="Equipment"
                placeholder="Search equipment"
                mandatory={requireEquipment}
                error={errors.equipmentId?.message}
                value={equipmentSearch}
                onChange={setEquipmentSearch}
                options={filteredEquipment.map(({ id, name }) => ({ label: name, value: id }))}
                onItemSelect={(id) => {
                  field.onChange(id ?? '');
                  if (id) {
                    const selected = equipmentList.find((e) => e.id === id);
                    if (selected) {
                      setEquipmentSearch('');
                    }
                  }
                }}
                selectedOptionLabel={selectedEquipmentName}
              />
            );
          }}
        />
      )}
    </Can>
  );

  return (
    <Modal open onClose={requestClose} title={title}>
      <form
        className={styles.form}
        onSubmit={(e) => {
          // Enter moves the wizard on rather than saving from the first step.
          e.preventDefault();
          e.stopPropagation();
          void (isEdit || isReview ? save() : handleNext());
        }}
      >
        {stepId && (
          <WizardStepper
            steps={steps.map((id) => ({ label: STEP_LABELS[id] }))}
            step={step}
            furthest={furthest}
            isSkippable={stepId === 'people' || stepId === 'details'}
            onGoToStep={moveTo}
            labelRef={focusRef}
          />
        )}

        {stepId === 'task' && (
          <>
            {!initialData && (
              <button
                className={styles.toggleArea}
                onClick={() => setIsPrivate(!isPrivate)}
                type="button"
              >
                <div className={styles.toggeStartBox}>
                  <span className={styles.toggleIcon}>
                    {isPrivate ? <FiLock size={20} /> : <FiUsers size={20} />}
                  </span>
                  <dd>
                    <b>{isPrivate ? 'Private Task' : 'Visible to Crew'}</b>
                    <p>{isPrivate ? 'Only you can see it.' : 'All crew members can see it.'}</p>
                  </dd>
                </div>
                <div className={styles.toggeEndtBox}>
                  <Toggle checked={isPrivate} onChange={() => setIsPrivate(!isPrivate)} />
                  <span>{isPrivate ? 'Switch to Public' : 'Switch to Private'}</span>
                </div>
              </button>
            )}

            <Input
              label="Task Name"
              placeholder="Change the watermaker filters"
              mandatory
              error={errors.taskName?.message}
              {...register('taskName')}
            />
            {!hidePriority && (
              <Controller
                name="priority"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    label="Priority"
                    mandatory
                    options={TASK_PRIORITIES.map(({ id, label }) => ({ label, value: id }))}
                    error={errors.priority?.message}
                  />
                )}
              />
            )}
            {requireEquipment && equipmentField}
          </>
        )}

        {stepId === 'when' && (
          <>
            {/* Only where the caller can save a series: `onSubmitTask` takes single tasks,
                so a repeat chosen there would be dropped without a word. */}
            {!eventId &&
              !hideRepeat &&
              !!onSubmitCallback &&
              (!initialData?.generator_id || editMode === 'all') && (
                <Controller
                  name="repeat"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      label="Repeat"
                      options={REPEAT_OPTIONS.map(({ id, label }) => ({
                        label,
                        value: id,
                      }))}
                      error={errors.repeat?.message}
                    />
                  )}
                />
              )}
            {isSingle ? (
              <DateTimePicker
                label="Dates"
                placeholder="Choose dates"
                mandatory
                endRequired
                minDate={eventDates?.start ?? undefined}
                maxDate={eventDates?.end ?? undefined}
                value={startDateValue || null}
                endValue={deadlineDateValue || null}
                onRangeChange={(start, end) => {
                  setValue('startDateTime', start ?? '', {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                  setValue('deadlineDateTime', end ?? '', {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }}
                error={errors.deadlineDateTime?.message ?? errors.startDateTime?.message}
                warning={
                  isStartInPast ? 'This task is in the past. Please check the date.' : undefined
                }
              />
            ) : (
              <>
                {repeatValue === RepeatOption.SPECIFIC_DAYS && (
                  <WeekdaySelector selectedDays={daysValue || []} onDayToggle={handleDayToggle} />
                )}
                <Controller
                  name="duration"
                  control={control}
                  render={({ field }) => (
                    <DurationInput
                      label="Duration"
                      mandatory
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.duration?.message}
                    />
                  )}
                />
                <div className={formStyles.InputRoot}>
                  <DateTimePicker
                    label="Start Date & Time"
                    placeholder="Jul 18, 2026"
                    mandatory
                    value={startDateValue || null}
                    onChange={(value) => {
                      setValue('startDateTime', value, { shouldValidate: true, shouldDirty: true });
                    }}
                  />
                  {errors.startDateTime && (
                    <p className={formStyles.Error}>{errors.startDateTime.message}</p>
                  )}
                  {!errors.startDateTime && isStartInPast && (
                    <p className={formStyles.Warning}>
                      <CiWarning size={16} />
                      This task is in the past. Please check the date.
                    </p>
                  )}
                </div>
              </>
            )}
          </>
        )}

        {stepId === 'people' && (
          <>
            {showAssignee && assigneeId && (
              <div className={styles.assigneeReadOnly}>
                <p className={styles.assigneeReadOnlyLabel}>Assignee</p>
                <div className={styles.assigneeReadOnlyValue}>
                  <ParticipantBadge
                    participantId={assigneeId}
                    firstName={assigneePerson?.firstName || assigneeFallbackName}
                    lastName={assigneePerson?.lastName}
                    avatarUrl={assigneePerson?.avatar}
                  />
                </div>
              </div>
            )}
            {!assigneeId && (
              <PeopleSelectionModal
                people={people || []}
                selectedIds={responsible?.person_id ? [responsible.person_id] : []}
                onSelect={(id) => {
                  const person = mappedPeople.find((p) => p.id === id);
                  const fullName =
                    [person?.firstName, person?.lastName].filter(Boolean).join(' ') || 'Unknown';
                  setResponsible({
                    person_id: id,
                    type: TaskParticipantType.CREW,
                    status: null,
                    name: fullName,
                  });

                  setSelectedPeople((prev) => prev.filter((p) => p.person_id !== id));
                }}
                onRemove={() => setResponsible(null)}
                triggerLabel="Add Assignee"
                modalTitle="Add Assignee"
                emptyMessage="No people match. Please try a different search."
                label="Assignee"
                maxSelections={1}
                away={away}
              />
            )}
            <PeopleSelectionModal
              people={
                people?.filter(
                  (p) =>
                    p.id !== responsible?.person_id && !pinnedPeople.some((pp) => pp.id === p.id),
                ) || []
              }
              selectedIds={[
                ...selectedPeople
                  .filter((p) => !pinnedPeople.some((pp) => pp.id === p.person_id))
                  .map((p) => p.person_id),
                ...pinnedPeople.map((p) => p.id),
              ]}
              onSelect={handleSelectPerson}
              onRemove={handleRemovePerson}
              triggerLabel="Add Participants"
              modalTitle="Add Participants"
              emptyMessage="No people match. Please try a different search."
              label="Participants"
              pinnedPeople={pinnedPeople}
              showAddAll
              addAllLabel="Add All Participants"
              away={away}
            />
          </>
        )}

        {stepId === 'details' && (
          <>
            {!requireEquipment && equipmentField}
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  label="Status"
                  options={taskStatuses.map(({ id, label, disabled }) => ({
                    label,
                    value: id,
                    disabled,
                  }))}
                  error={errors.status?.message}
                />
              )}
            />
            {/* Off by default: a plain task is just something to do, and pricing it is noise.
                The maintenance and service surfaces opt in with `showCost`.
                Only single tasks carry a cost either way — an activity generator has no such
                field, so a value typed here for a repeating task would be dropped on save. */}
            {showCost && isSingle && (
              <Controller
                name="cost"
                control={control}
                render={({ field }) => (
                  <Input
                    label="Estimated Cost"
                    placeholder="0"
                    type="number"
                    min={0}
                    step="0.01"
                    error={errors.cost?.message}
                    value={field.value ?? ''}
                    onChange={(e) => {
                      const raw = e.target.value;
                      field.onChange(raw === '' ? null : Number(raw));
                    }}
                    onBlur={field.onBlur}
                    ref={field.ref}
                  />
                )}
              />
            )}
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <RichTextArea
                  label="Description"
                  placeholder="Description"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  error={errors.description?.message}
                />
              )}
            />
            <Controller
              name="checklist"
              control={control}
              render={({ field }) => (
                <ChecklistEditor value={field.value ?? []} onChange={field.onChange} />
              )}
            />
            {/* Only single tasks carry files: a repeating task is a rule an activity
                generator runs, and the generator has nothing to attach them to. */}
            {isSingle && !staged && (
              <TaskFiles
                documents={documents}
                pendingFiles={pendingFiles}
                onAttach={(files) => setPendingFiles((prev) => [...prev, ...files])}
                onRemoveDocument={handleRemoveDocument}
                onRemovePending={(index) =>
                  setPendingFiles((prev) => prev.filter((_, i) => i !== index))
                }
                isBusy={isSubmitting}
              />
            )}
          </>
        )}

        {isReview && (
          <section ref={focusRef} tabIndex={-1} aria-label="Review">
            <StepReview items={reviewItems} noun="Task" />
          </section>
        )}

        {/* Create: Next walks the steps, and once the required ones are done the task can
            be added from any step after. Edit: every step is open, so Save is always there. */}
        <div className={styles.wizardActions}>
          {step > 1 && <ButtonOutlined onClick={() => moveTo(step - 1)}>Back</ButtonOutlined>}
          <span className={styles.wizardActionsSpacer} />
          {isEdit ? (
            <>
              {step < steps.length && (
                <ButtonOutlined onClick={() => void handleNext()}>Next</ButtonOutlined>
              )}
              <Button onClick={() => void save()} disabled={isSubmitting || !isTaskStepComplete}>
                {buttonText}
              </Button>
            </>
          ) : isReview ? (
            <Button onClick={() => void save()} disabled={isSubmitting}>
              {buttonText}
            </Button>
          ) : (
            <>
              {step > 1 && (
                <ButtonOutlined onClick={() => void save()} disabled={isSubmitting}>
                  {buttonText}
                </ButtonOutlined>
              )}
              <Button
                onClick={() => void handleNext()}
                disabled={stepId === 'task' && !isTaskStepComplete}
              >
                Next
              </Button>
            </>
          )}
        </div>
      </form>
      <ConfirmationModal
        open={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        onConfirm={onClose}
        title="Leave Without Saving?"
        message="What you’ve entered won’t be saved."
        confirmText="Discard Changes"
        cancelText="Keep Editing"
      />
    </Modal>
  );
};

const CreateEditTaskSheet = ({ open, ...props }: CreateEditTaskSheetProps) => {
  const { people, getPeople } = usePerson();

  // Re-read on every open, not only the first: the list is session-wide, and
  // crew added since it was fetched would otherwise be missing from Assignee.
  useEffect(() => {
    if (open) void getPeople();
  }, [open, getPeople]);

  return open ? (
    <CreateEditTaskSheetContent
      key={props.initialData?.id || 'new-task'}
      {...props}
      people={people}
    />
  ) : null;
};

export default CreateEditTaskSheet;
